import axios from '@sas/nova-core/axios';
import { getLogger } from '@sas/nova-core/logging';

import {
  getUsersFromSearchFavoritesResults,
  getUsersFromSearchResults,
  SearchFavoritesRESTResponse,
  SearchRESTResponse,
} from '../../../services/catalogService';
import { UpdateHistoryFolderMembersRESTResponse } from '../../../services/foldersService';
import { fetchBody } from '../../../util/service';

import {
  SearchResultBaseJSON,
  SearchResultCollectionJSON,
  SearchResultRepr,
} from '../../catalog/search/representations';

import { DataSourcesProviderCompute } from '../../data-discovery-commons/client/data/api';
import { ExtractProviderAndServer } from '../../data-discovery-commons/client/data/resolver_fast';
import { FindLinkByRel } from '../../data-discovery-commons/client/rest/api';
import { GetTableDetailsByEntity } from '../../data-discovery-commons/service/data';

import { RequestParamFilter, RequestParamLimit, RequestParamStart } from '../../sonder/rest/paging';
import { CollectionMediaTypeJSON } from '../../sonder/rest/representations/commons/commons';
import {
  ResourceSelection,
  SelectionMediaTypeJSON,
} from '../../sonder/rest/representations/commons/resource_selection';
import {
  MediaTypeFolderJSON,
  MediaTypeMemberJSON,
  Member,
  MemberTypeReference,
  MemberVersion,
} from '../../sonder/rest/representations/folders/folders';
import {
  AssetDetailsRequest,
  AssetDetailsResponse,
  Entity,
  GetFavoriteResultsResponse,
  GetHistoryFolderMembersResponse,
  GraphVariable,
  HistoryFolderMembersRequest,
  ObjectTypesResponse,
  RegisteredTypeObject,
  RemoveHistoryFolderMembersResponse,
  Result,
  ResultsFolderMember,
  SearchFavoritesRequest,
  SearchFavoritesResponse,
  SearchIndicesResponse,
  SearchIndex,
  SearchRequest,
  SearchResponse,
  UpdateHistoryFolderMembersRequest,
  UpdateHistoryFolderMembersResponse,
  User,
  SearchFacetsResponse,
} from '../grpc_web_catalog/catalog.pb';

import {
  GO_MAX_INT32,
  GetSearchIndices as getCatalogSearchIndices,
  GetSearchSuggestions as getCatalogSearchSuggestions,
  GetSearchFacets as getCatalogSearchFacets,
} from './service/catalog/access/catalogServiceAccessorREST';
import { GetTypes as typesGetTypes } from './service/types/access/typesServiceAccessorREST';
import { FindMapHref } from './util/util';

// eslint-disable-next-line @sas/i18n/jsx-to-localize
const logger = getLogger('DataSelection');

// 247
const collectionItemToResult = (item: SearchResultRepr): Result => {
  const resourceuri = FindMapHref(item.links!, 'resource') || '';
  // DSD add begin
  let dsdAdditions = {};
  const { provider, server } = ExtractProviderAndServer(resourceuri);
  if (provider !== DataSourcesProviderCompute) {
    dsdAdditions = {
      server,
    };
  }
  // DSD add end

  return {
    resourceuri,
    selfuri: FindMapHref(item.links!, 'self') || '',

    attributes: item.attributes,

    rowcount: item.attributes?.rowCount, // AttributeToUInt64Value
    columncount: item.attributes?.columnCount, // AttributeToUInt32Value
    datasize: getDataSize(item.attributes?.dataSize, item.attributes?.rowCount),
    completenesspercent: item.attributes?.completenessPercent, // AttributeToDoubleValue
    library: item.attributes?.library || '', // AttributeToString

    region: item.attributes?.region || '', // AttributeToString // DSD+
    owner: item.owner || '', // AttributeToString // DSD+
    modifiedby: item.modifiedBy || '', // AttributeToString // DSD+
    mintimestamp: item.attributes?.minTimeStamp || '', // AttributeToString // DSD+
    maxtimestamp: item.attributes?.maxTimeStamp || '', // AttributeToString // DSD+

    // DSD+ The following are properties on Result that need to be copied from item.
    // The Go service does unmarshalling to automatically cherry-pick the applicable
    // Result properties over.  That's not an option here.
    id: item.id,
    name: item.name,
    modified: item.modified,
    created: item.created,
    score: item.score,
    type: item.type,
    registeredtype: item.registeredType,
    displaytype: item.displayType,

    ...dsdAdditions,
  };
};

// 289
// Search - grpc endpoint for handling the catalog microservice's search functionality
export const Search = async (req: SearchRequest): Promise<SearchResponse> => {
  const params: any = {
    q: req.query,
    type: req.type,
    filterReferences: 'true',
    [RequestParamStart]: req.start,
    [RequestParamLimit]: req.limit,
    timezone: req.timezone,
  };
  if (req.indices.length > 0) {
    params.indices = req.indices.join(',');
  }

  const response = await fetchBody({
    method: 'GET',
    url: '/catalog/search',
    params,
    headers: {
      Accept: SearchResultCollectionJSON,
      'Accept-Item': SearchResultBaseJSON,
    },
  });

  const collection = response as SearchRESTResponse;
  const searchResults: Result[] = [];

  let nextStart = -1;
  // https://rndjira.sas.com/browse/DATAPREP-25306
  // pull the start value from the next link if it's there and send that back to the UI since it can
  // differ from (previous start + items length)
  // /catalog/search?start=10&limit=10
  const nextLink = FindLinkByRel(collection.links, 'next');
  if (nextLink) {
    try {
      const parsed = new URL(nextLink.uri, window.location.href);
      const startStr = parsed.searchParams.get(RequestParamStart);
      if (startStr) {
        const start = parseInt(startStr);
        if (isNaN(start)) {
          logger.debug(`The start parameter on the next link was an invalid value '${startStr}'`);
        } else {
          logger.debug(`Setting Next Start value to ${start}`);
          nextStart = start;
        }
      }
    } catch (err) {
      logger.debug(`There was an error parsing the next link ${err}`);
    }
  } else {
    logger.debug(
      'The next link was not present on the search response. There must not be any more matches'
    );
    logger.debug(`Setting Next Start value to ${nextStart}`);
  }

  // collect user details up front.  it's easier to fill these in while processing each
  // search result than updating each result later.
  const knownUsers = await getUsersFromSearchResults(collection.items);

  const collectionLength = collection.items.length;
  if (collectionLength > 0) {
    // userCacheMap := make(map[string]*grpc_web_catalog.User)
    for (const result of collection.items) {
      const a = collectionItemToResult(result);
      a.owner = getUserName(a.owner, knownUsers);
      a.modifiedby = getUserName(a.modifiedby, knownUsers);
      searchResults.push(a);
    }
  }

  return {
    results: searchResults,
    count: collection.count!,
    maxscore: collection.maxScore,
    nextstart: nextStart,
  };
};

// 363
// SearchFavorites - grpc endpoint for retrieving search result information using resourceUris
export const SearchFavorites = async (
  req: SearchFavoritesRequest
): Promise<SearchFavoritesResponse> => {
  const selection: ResourceSelection = {
    resources: req.resourceuris,
  };

  // DSD add begin
  // save a catalog REST call if there are no potential items to query
  if (req.resourceuris.length === 0) {
    return { results: [] };
  }
  // DSD add end

  let response;
  try {
    if (req.limit > 10000 || req.limit < 0) {
      req.limit = 10000;
    }
    const params: any = {
      filterReferences: 'true',
      [RequestParamStart]: req.start,
      [RequestParamLimit]: req.limit,
    };
    if (req.indices && req.indices.length > 0) {
      params.indices = req.indices.join(',');
    }

    response = await axios.post('/catalog/search/favorites', JSON.stringify(selection), {
      headers: { 'Content-Type': SelectionMediaTypeJSON },
      params,
    });
  } catch (error: any) {
    if (error.response.status === 404) {
      return {
        results: [],
      };
    }
    throw 'call to /catalog/search/favorites failed';
  }

  const respRecords = response.data as SearchFavoritesRESTResponse;
  const results: Result[] = [];
  const searchFavoritesResponse: SearchFavoritesResponse = { results: [] };

  // collect user details up front.  it's easier to fill these in while processing each
  // search result than updating each result later.
  const knownUsers = await getUsersFromSearchFavoritesResults(respRecords);

  for (const id in respRecords) {
    const valueResults = respRecords[id];
    const resultId = valueResults.id || ''; // AttributeToString

    if (resultId !== '') {
      const owner = valueResults.owner || ''; // AttributeToString
      const modifiedBy = valueResults.modifiedBy || ''; // AttributeToString

      const resourceURI = FindMapHref(valueResults.links, 'resource');

      const result: Result = {
        id: resultId, // AttributeToString
        name: valueResults.name || '', // AttributeToString
        modified: valueResults.modified || '', // AttributeToString
        created: valueResults.created || '', // AttributeToString
        type: valueResults.type || '', // AttributeToString
        registeredtype: valueResults.registeredType || '',
        displaytype: valueResults.displayType || '',
        score: valueResults.score,

        // lookup full user names
        owner: getUserName(owner, knownUsers),
        modifiedby: getUserName(modifiedBy, knownUsers),

        attributes: valueResults.attributes,

        rowcount: valueResults.attributes?.rowCount, // AttributeToUInt64Value
        columncount: valueResults.attributes?.columnCount, // AttributeToUInt32Value
        datasize: getDataSize(valueResults.attributes?.dataSize, valueResults.attributes?.rowCount),
        completenesspercent: valueResults.attributes?.completenessPercent, // AttributeToDoubleValue
        library: valueResults.attributes?.library || '', // AttributeToString
        region: valueResults.attributes?.region || '', // AttributeToString // DSD+
        mintimestamp: valueResults.attributes?.minTimeStamp || '', // AttributeToString
        maxtimestamp: valueResults.attributes?.maxTimeStamp || '', // AttributeToString

        resourceuri: id,
      };
      if (resourceURI) {
        result.resourceuri = resourceURI;
        // DSD add begin
        const { provider, server } = ExtractProviderAndServer(resourceURI);
        if (provider !== DataSourcesProviderCompute) {
          result.server = server;
        }
        // DSD add end
      }
      const selfURI = FindMapHref(valueResults.links, 'self');
      if (selfURI) {
        result.selfuri = selfURI;
      }

      results.push(result);
    }
  }

  searchFavoritesResponse.results = results;
  return searchFavoritesResponse;
};

// 678
// GetSearchIndices - gets all search indices available in catalog
export const GetSearchIndices = async (): Promise<SearchIndicesResponse> => {
  const response = await getCatalogSearchIndices();

  const searchIndices: SearchIndex[] = [];
  for (const searchIndex of response.items) {
    searchIndices.push({
      name: searchIndex.name,
      typeDefinition: searchIndex.typeDefinition,
      stageName: searchIndex.stageName,
      properties: searchIndex.properties,
      children: searchIndex.children,
      iconSource: searchIndex.iconSource,
      label: searchIndex.label,
    });
  }

  return { indices: searchIndices };
};

// 757
// GetAssetDetails - grpc endpoint for getting asset details for a search result
export const GetAssetDetails = async (
  request: AssetDetailsRequest
): Promise<[AssetDetailsResponse | undefined, Error?]> => {
  const [table, err] = await GetTableDetailsByEntity(request.selfUri, {
    authCheck: !request.skipAuthCheck,
    extendedDetails: true,
    resolveComputeContextName: true,
  });

  if (err || !table) {
    return [undefined, err];
  }
  const e = table.extendedTableAttributes?.entity;
  let entity: Entity | undefined;

  if (e) {
    entity = {
      id: e.id,
      name: e.name,
      label: e.label,
      creationtimestamp: table.dateCreated,
      modifiedtimestamp: table.dateModified,
      type: e.type,
      description: e.description,
      definitionid: e.definitionID,
      resourceid: e.resourceID,
      modifiedby: e.modifiedBy,
      createdby: e.createdBy,
      attributes: e.attributes,
      selfuri: request.selfUri,
    };
  }
  const response: AssetDetailsResponse = {
    entity,
    tablename: table.name,
    encoding: table.encoding,
    dateanalyzed: table.dateAnalyzed,
    datecreated: table.dateCreated,
    datemodified: table.dateModified,
    description: table.description,
    label: table.label,
    datalocale: table.dataLocale,

    responsibleparty: table.responsibleParty,
    createdby: table.createdBy,
    modifiedby: table.modifiedBy,
    sourcetablename: table.sourceTableName,
    attributes: e?.attributes,
    engine: table.engine,
    path: table.path,
    region: table.region,
    library: table.library,
    server: table.server,
    columncount: table.columnCount,
    rowcount: table.rowCount,
    datasizebytes: table.dataSizeBytes,
  };

  if (table.stats) {
    response.completenesspercent = table.stats.completenessPercent;
    response.informationprivacy = table.stats.informationPrivacy;
    response.mintimestamp = table.stats.minTimestamp;
    response.maxtimestamp = table.stats.maxTimestamp;
  } else {
    logger.debug(`The table status information was not available for ${request.selfUri}`);
  }
  if (table.extendedTableAttributes?.history) {
    const timestampValues: string[] = [];
    for (const [
      i,
      val,
    ] of table.extendedTableAttributes.history.analysisTimestamp.values.entries()) {
      timestampValues[i] = val;
    }
    const dateVariable: GraphVariable = {
      label: table.extendedTableAttributes.history.analysisTimestamp.label,
      isnumeric: table.extendedTableAttributes.history.analysisTimestamp.numeric,
      stringsList: timestampValues,
    };

    const rowCountValues: number[] = [];
    for (const [i, val] of table.extendedTableAttributes.history.rowCount.values.entries()) {
      rowCountValues[i] = val;
    }
    const rowCountVariable: GraphVariable = {
      label: table.extendedTableAttributes.history.rowCount.label,
      isnumeric: table.extendedTableAttributes.history.rowCount.numeric,
      numbersList: rowCountValues,
    };

    const completenessValues: number[] = [];
    for (const [i, val] of table.extendedTableAttributes.history.completeness.values.entries()) {
      completenessValues[i] = val;
    }
    const completenessVariable: GraphVariable = {
      label: table.extendedTableAttributes.history.completeness.label,
      isnumeric: table.extendedTableAttributes.history.completeness.numeric,
      numbersList: completenessValues,
    };

    response.graphvariablesList = [dateVariable, rowCountVariable, completenessVariable];
  } else {
    logger.debug(`The table history information was not available for ${request.selfUri}`);
  }

  return [response, undefined];
};

// 510
// GetSearchSuggestions - gets type ahead search suggestions for the given field
export const GetSearchSuggestions = async (props: {
  indices: Array<string>;
  fieldName: string;
  initialValue: string;
  limit: number;
}): Promise<Array<string>> => {
  const response = await getCatalogSearchSuggestions({
    facet: props.fieldName,
    indices: props.indices,
    value: props.initialValue,
    limit: props.limit,
  });
  return response.items;
};

// 624
// GetSearchFacets - gets the available search facets for all indices in the catalog
export const GetSearchFacets = async (): Promise<SearchFacetsResponse> => {
  const response = await getCatalogSearchFacets();

  return { facets: response.items };
};

// 872
// Deprecated use data-discovery-commons service.IdentitiesService.GetUserFromCache
const getUser = (userId: string, userMap: Map<string, User>) => {
  if (userId === '') {
    return undefined;
  }
  const foundUser = userMap.get(userId)!;
  if (!foundUser) {
    return {
      id: userId,
      name: userId,
      title: '',
    };
  } else {
    return {
      id: foundUser.id,
      name: foundUser.name,
      title: foundUser.title,
    };
  }
};

// 906
const getUserName = (userID: string, userCacheMap: Map<string, User>): string => {
  const user = getUser(userID, userCacheMap);
  if (user === undefined || user.name === undefined) {
    return '';
  }
  return user.name;
};

// 3126
// GetFavoriteResults returns the entities from the search index that correspond to members of the favorites folder
export const GetFavoriteResults = async (
  indices: Array<string>
): Promise<GetFavoriteResultsResponse> => {
  const uri = '/folders/folders/@myFavorites/members';

  try {
    const response = await fetchBody({
      method: 'GET',
      url: uri,
      params: {
        [RequestParamLimit]: GO_MAX_INT32,
      },
      headers: {
        Accept: CollectionMediaTypeJSON,
        'Accept-Item': MediaTypeFolderJSON,
      },
    });

    // use map so we don't get any overlaps
    const resourceUris: Map<string, boolean> = new Map();
    for (const folderMember of response.items) {
      resourceUris.set(folderMember.uri, true);
    }
    const resourceURIList = [...resourceUris.keys()];

    const searchReq: SearchFavoritesRequest = {
      resourceuris: resourceURIList,
      indices,
      start: 0,
      limit: GO_MAX_INT32,
    };

    const searchResp = await SearchFavorites(searchReq);
    return {
      results: searchResp.results,
    };
  } catch (error) {
    throw Error(`Problem with GET ${uri}):\n${error}`);
  }
};

export const GetFavoritesFolderMemberUris = async (): Promise<Array<string>> => {
  const uri = '/folders/folders/@myFavorites/members';

  const response = await fetchBody({
    method: 'GET',
    url: uri,
    params: {
      [RequestParamLimit]: GO_MAX_INT32,
    },
    headers: {
      Accept: CollectionMediaTypeJSON,
      'Accept-Item': MediaTypeFolderJSON,
    },
  });
  // use map so we don't get any overlaps
  const resourceUris: Map<string, boolean> = new Map();
  for (const folderMember of response.items) {
    resourceUris.set(folderMember.uri, true);
  }
  const resourceURIList = [...resourceUris.keys()];
  return resourceURIList;
};

// 3482
export const UpdateHistoryFolderMembers = async (
  req: UpdateHistoryFolderMembersRequest
): Promise<UpdateHistoryFolderMembersResponse> => {
  const uri = req.item.uri;
  const resourceURI = uri;
  // Add a child member to my folder
  const member1: Member = {
    version: MemberVersion,
    name: req.item.name,
    description: '',
    uri: resourceURI,
    type: MemberTypeReference,
    contentType: req.item.contentType,

    createdBy: req.item.createdBy,
  };

  // By adding the creation timestamp we can prevent the folders service from hitting the uri provided,
  // as that is very bad when it is a compute table. If session isn't provided (and the folders service)
  // doesn't support passing along a compute session, then a new compute session has to be spun up - each
  // taking >10 seconds, then being abandoned.
  // DSD+ going to assume that ISO-8601 is just a more strict version of RFC-3339 (which Information Catalog
  // midtier uses)
  member1.creationTimeStamp = req.item.creationTimeStamp;

  let response;
  const historyUri = '/folders/folders/@myHistory/members';
  try {
    const resp = await axios.post(historyUri, JSON.stringify(member1), {
      headers: { 'Content-Type': MediaTypeMemberJSON, Accept: MediaTypeMemberJSON },
    });
    response = resp.data as UpdateHistoryFolderMembersRESTResponse;
  } catch (error) {
    throw 'call to POST /folders/folders/@myHistory/members failed';
  }
  return {
    item: response,
  };
};

// 3541
export const GetHistoryFolderMembers = async (
  req: HistoryFolderMembersRequest
): Promise<GetHistoryFolderMembersResponse> => {
  const uri = '/folders/folders/@myHistory/members';
  let optionalParms = {};

  if (req.name && req.name.length > 0) {
    // single quotes need to be doubled in the parameter values
    // https://rndconfluence.sas.com/confluence/display/APISNS/Literals
    const name = req.name.replace(/\'/g, "''");
    optionalParms = {
      [RequestParamFilter]: `eq(name,'${name}')`,
    };
  }

  try {
    const response = await fetchBody({
      method: 'GET',
      url: uri,
      params: {
        [RequestParamLimit]: GO_MAX_INT32,
        ...optionalParms,
      },
      headers: {
        Accept: CollectionMediaTypeJSON,
        'Accept-Item': MediaTypeFolderJSON,
      },
    });
    const historyResponse: GetHistoryFolderMembersResponse = {
      items: response.items as ResultsFolderMember[],
    };
    return historyResponse;
  } catch (error) {
    throw Error(`Problem with GET ${uri}):\n${error}`);
  }
};

// 3579
export const RemoveHistoryFolderMembers = async (
  req: UpdateHistoryFolderMembersRequest
): Promise<RemoveHistoryFolderMembersResponse | undefined> => {
  const tablePrefix = '/folders/folders/';
  const historyFolder = req.parentFolderUri;
  if (historyFolder && historyFolder.length > 0 && historyFolder.startsWith(tablePrefix)) {
    const historyPath = historyFolder.substring(tablePrefix.length);
    const uri = `/folders/folders/${historyPath}/members/${req.item.id}`;
    try {
      await axios.delete(uri);
      return {};
    } catch (err) {
      throw err;
    }
  }
};

// 3920
// GetTypes - gets all registered object types from the types service
export const GetTypes = async (): Promise<ObjectTypesResponse> => {
  const response = await typesGetTypes();

  const fixedTypes: RegisteredTypeObject[] = [];
  for (const item of response.items) {
    fixedTypes.push({
      version: item.version,
      name: item.name,
      label: item.label,
      plurallabel: item.pluralLabel,
      mediatype: item.mediaType,
      description: item.description,
      iconuri: item.iconUri,
      resourceuri: item.resourceUri,
      servicerooturi: item.serviceRootUri,
      tags: item.tags,
      mappedtypes: item.mappedTypes,
      properties: item.properties,
      mediatypes: item.mediaTypes,
      extensions: item.extensions,
    });
  }

  return { types: fixedTypes };
};

// 3810
const getDataSize = (dataSize: number | undefined, rowCount: number | undefined) => {
  if (dataSize !== undefined && dataSize === 0 && rowCount !== undefined && rowCount > 0) {
    return undefined;
  }
  return dataSize;
};
